<template>
    <div class="warehouse-admins-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
        >
            <template #loading>
                <div class="warehouse-admins-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #top>
                <div class="kasanowa-top-filter-wrapper">
                    <KTextField
                        id="search"
                        v-model="search"
                        name="search"
                        label=""
                        :placeholder="$t('common.search')"
                        hide-details
                        clearable
                    />
                </div>
            </template>

            <template #item.email="elem">
                <div v-if="elem.item.email != null">{{ elem.item.email }}</div>
            </template>

            <template #item.actions="elem">
                <div v-if="isAdminColian || isAdmin || isPhReg" class="warehouse-admins-table__action-wrapper" @click.stop>
                    <KTooltip without-btn>
                        <template v-slot:title>
                            <div class="kasanowa-hover warehouse-admins-table__icon-wrapper warehouse-admins-table__icon-wrapper--edit" @click="navigateTo(`/admin/${elem.item.id}`)">
                                <KIcon icon="edit" :color="grey400" />
                            </div>
                        </template>
                        <template v-slot:content>
                            <div>{{ $t('warehouse.edit') }}</div>
                        </template>
                    </KTooltip>
                    <KTooltip without-btn>
                        <template v-slot:title>
                            <div class="kasanowa-hover warehouse-admins-table__icon-wrapper warehouse-admins-table__icon-wrapper--trash" @click="openDeleteAdminDialog(elem.item.id)">
                                <KIcon icon="trash" :color="grey400" />
                            </div>
                        </template>
                        <template v-slot:content>
                            <div>{{ $t('warehouse.delete') }}</div>
                        </template>
                    </KTooltip>
                </div>
            </template>

            <template #footer.prepend>
                <div class="kasanowa-total-table-counter">{{ $t('common.total') }}: {{ total }}</div>
            </template>
        </v-data-table-server>

        <KDialog
            :is-open-dialog-action="isOpenDeleteDialog"
            :is-close-dialog-action="isCloseDeleteDialog"
            close-header-btn
            @close-dialog="closeDialogWhenNoTargetPressed"
        >
            <template v-slot:default>
                <div>
                    <div class="warehouse-admins-table__dialog-title">{{ $t('admin.areYouSureDeleteAdmin') }}</div>
                    <div class="warehouse-admins-table__btn-dialog-wrapper">
                        <KButton
                            id="admin-cancel"
                            :label="$t('admin.cancel')"
                            secondary
                            @click="closeDialogWhenNoTargetPressed"
                        />
                        <KButton
                            id="admin-delete"
                            :label="$t('admin.deleteBtn')"
                            type="submit"
                            secondary
                            :loading="loadingDeleteBtn"
                            @click="deleteAction"
                        />
                    </div>
                </div>
            </template>
        </KDialog>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import router from '@/router';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import AdminApi from '@/api/v1/AdminApi';
import { toast } from 'vue3-toastify';
import { navigateTo } from '@/hooks/NavigateHooks';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type { IAdminResponse } from '@/models/AdminModel';
import variables from '@/assets/scss/modules/variables.module.scss';
import KIcon from '@/components/atoms/KIcon.vue';
import KTooltip from '@/components/atoms/KTooltip.vue';
import KButton from '@/components/atoms/KButton.vue';
import KDialog from '@/components/atoms/KDialog.vue';
import KTextField from '@/components/atoms/KTextField.vue';

const { isAdminColian, isAdmin, isPhReg } = storeToRefs(useUserStore());
const { grey400 } = variables;

const headers = ref<ITableHeader[]>([
    {
        title: t('admin.emailTable'),
        align: 'start',
        sortable: false,
        key: 'email',
        width: '90%',
        minWidth: '90%',
    },
    {
        title: '',
        align: 'center',
        sortable: false,
        key: 'actions',
        width: '',
        minWidth: '160px',
    },
]);
const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<IAdminResponse[]>([]);
const adminApi = new AdminApi();
const warehouseAdminList = async() => {
    loading.value = true;
    try {
        const result = await adminApi.adminsList(
            router.currentRoute.value.params.id,
            options.value.page,
            options.value.itemsPerPage,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].key : null,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].order : null,
            search.value,
        );
        if (!result.isSuccess) {
            toast.error(t('admin.toast.errorAdminList'));
            loading.value = false;
            return;
        }
        currentPage.value = result.payload.current_page;
        lastPage.value = result.payload.last_page;
        total.value = result.payload.total;
        items.value = result.payload.data;
    } catch (e) {
        toast.error(t('admin.toast.errorAdminList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
    warehouseAdminList();
};

const isOpenDeleteDialog = ref<boolean>(false);
const isCloseDeleteDialog = ref<boolean>(true);
const selectedAdminId = ref<number | null>(null);
const closeDialogWhenNoTargetPressed = () => {
    isOpenDeleteDialog.value = false;
    isCloseDeleteDialog.value = true;
    selectedAdminId.value = null;
};
const openDeleteAdminDialog = (id: number) => {
    selectedAdminId.value = id;
    isOpenDeleteDialog.value = true;
    isCloseDeleteDialog.value = false;
};
const loadingDeleteBtn = ref<boolean>(false);
const deleteAction = async() => {
    loadingDeleteBtn.value = true;
    try {
        const result = await adminApi.deleteAdmin(selectedAdminId.value);
        if (!result.isSuccess) {
            toast.error(t('admin.toast.errorAdminDeleteAction'));
            loadingDeleteBtn.value = false;
            closeDialogWhenNoTargetPressed();
            return;
        }
        toast.success(t('admin.toast.successAdminDeleteAction'));
        closeDialogWhenNoTargetPressed();
        warehouseAdminList();
    } catch (e) {
        toast.error(t('admin.toast.errorAdminDeleteAction'));
        loadingDeleteBtn.value = false;
        closeDialogWhenNoTargetPressed();
        return;
    }
    loadingDeleteBtn.value = false;
};

const search = ref<string | null>(null);
const searchInterval = ref<any>(undefined);
watch(() => search.value, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        clearTimeout(searchInterval.value);
    }
    searchInterval.value = setTimeout(() => {
        warehouseAdminList();
    }, 700);
}, {
    deep: true
});
</script>

<style scoped lang="scss">
.warehouse-admins-table {
    &__loading {
        opacity: 0.6;
    }

    &__action-wrapper {
        display: flex;
        justify-content: space-around;
    }

    &__icon-wrapper {
        padding: 5px;
        height: 26px;
    }

    &__btn-dialog-wrapper {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        @include media-breakpoint-up(sm) {
            margin-top: 20px;
            flex-direction: row;
            width: 100%;
            justify-content: space-around;
        }
    }

    &__dialog-title {
        font-size: 18px;
        letter-spacing: 0;
    }

    &:deep(.v-data-table tr:hover) {
        .warehouse-admins-table__icon-wrapper--edit svg {
            fill: $primary-800;
        }

        .warehouse-admins-table__icon-wrapper--trash svg {
            fill: $red-400;
        }
    }

    &:deep(.v-data-table td) {
        background-color: transparent !important;

        &:first-child {
            background: $white !important;
            color: $black !important;
        }
    }

    &:deep(.v-data-table tr:hover) {
        background-color: $secondary-400 !important;
        transition: 0.5s !important;

        td {
            background: $secondary-400 !important;
            color: $black !important;
        }
    }
}
</style>
